import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IBreadcrumbData } from '../../../../interfaces/breadcrumb-data.interface';
import { AuthenticationService } from '../../../../services/authentication.service';
import { SimpleDialogService } from '../../../../services/simple-dialog.service';
import { Observable } from 'rxjs';
import { EditComponent } from '../../components/edit/edit.component';
import { RightsComponent } from '../../components/rights/rights.component';
import { User } from '../../entities/user';
import { UserService } from '../../services/user.service';
import { SimpleDialogData } from '../../../../interfaces/simple-dialog-data.interface';

/**
 * Modal dimensions
 */
const MODAL_DIMENSIONS: object = {
  height: '700px',
  width: '1080px',
};

/**
 * List component
 */
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit {
  /**
   * Forbidden users
   */
  forbiddenUsers: string[] = ['GUEST', 'MASTERWEB', 'MASTERASP'];

  /**
   * Displayed columns
   */
  displayedColumns: string[] = ['name', 'password', 'language', 'action'];

  /**
   * Table config
   */
  tableConfig: string[] = ['Username', 'Password', 'Language', ''];

  /**
   * Users
   */
  users: Observable<User[]>;

  /**
   * BreadcrumbData
   */
  breadcrumbData: IBreadcrumbData;

  /**
   * Constructor
   *
   * @param userService UserService
   * @param dialog MatDialog
   * @param simpleDialogService SimpleDialogService
   */
  constructor(
    private userService: UserService,
    private dialog: MatDialog,
    private simpleDialogService: SimpleDialogService,
    private authenticationService: AuthenticationService
  ) {
    this.breadcrumbData = {
      titles: {
        icon: 'account_circle',
        links: [
          {
            text: 'User list',
          },
        ],
      },
      description: 'Here you can see all users, edit, crate and delete. Also assign rights to users.',
      buttons: [
        {
          text: 'Create',
          action: () => this.create(),
          icon: 'add',
          disabled: false
        },
      ],
    };
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    if (!this.isModuleAvailable('MATUSERS')) {
      this.breadcrumbData.buttons = [];
    }
    this.loadUsers();
  }

  /**
   * Check if user is protected
   *
   * @param user User
   *
   * @returns boolean
   */
  isUserProtected(user: User): boolean {
    if (user.name) {
      return this.forbiddenUsers.indexOf(user.name.toUpperCase()) > -1;
    }
  }

  /**
   * Open rights modal
   *
   * @param user User object
   */
  openRights(user: User): void {
    this.dialog.open(RightsComponent, {
      data: {
        user,
      },
      ...MODAL_DIMENSIONS,
    });
  }

  /**
   * Manage creation process
   */
  create(): void {
    const dialogRef = this.dialog.open(EditComponent);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  /**
   * Manage edition process
   *
   * @param user User object
   */
  edit(user: User): void {
    const dialogRef = this.dialog.open(EditComponent, {
      data: {
        userName: user.name,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  remove(user: User): void {
    const data: SimpleDialogData = {
      title: 'Warning!',
      message: `Do you want remove user ${user.name}?`,
      rightButtonText: 'Yes',
    };

    this.simpleDialogService.open(data).subscribe((response) => {
      if (response) {
        this.userService.delete(user.name).subscribe(() => this.loadUsers());
      }
    });
  }

  /**
   * Load users
   */
  loadUsers(): void {
    this.users = this.userService.list();
  }

  /**
   * Service is loading
   */
  isLoading(): Observable<boolean> {
    return this.userService.loading$;
  }

  isModuleAvailable(moduleId: string) {
    return this.authenticationService.isModuleAvailable(moduleId, true);
  }
}
